import { useEffect, useMemo, useState } from "react";
import {
  listTaxonomyNodes,
  listTaxonomyVersions,
  TaxonomyNodeRow,
  TaxonomyVersionRow,
} from "@/api/methodologyConsole";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AdvancedJson,
  EmptyState,
  ErrorState,
  HashLabel,
  LifecycleBadge,
  LoadingState,
  StatCard,
} from "./primitives";

const KINDS = [
  "sector",
  "subsector",
  "industry",
  "business_model",
  "service_model",
  "business_capability",
  "infrastructure_component",
  "assessment_type",
  "cyber_capability",
];

export default function TaxonomyTab() {
  const [versions, setVersions] = useState<TaxonomyVersionRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [kind, setKind] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [nodes, setNodes] = useState<TaxonomyNodeRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    listTaxonomyVersions()
      .then((v) => {
        setVersions(v);
        const current =
          v.find((x) => x.status === "internal_review") ??
          v.find((x) => x.status === "published") ??
          v[0];
        setSelected(current?.id ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setNodes(null);
    setPage(0);
    listTaxonomyNodes(selected, kind === "all" ? null : kind, search || null)
      .then(setNodes)
      .catch((e: Error) => setError(e.message));
  }, [selected, kind, search]);

  const version = useMemo(
    () => versions?.find((v) => v.id === selected) ?? null,
    [versions, selected],
  );

  const universal = useMemo(
    () =>
      (nodes ?? []).filter(
        (n) => (n.attributes as Record<string, unknown> | null)?.scope === "universal",
      ).length,
    [nodes],
  );

  const chains = useMemo(
    () =>
      (nodes ?? [])
        .filter((n) => n.kind === "business_capability")
        .flatMap((cap) =>
          (cap.outgoing_edges ?? [])
            .filter((e) => e.edge_type === "implemented_by")
            .map((e) => ({ capability: cap.label, component: e.to_label })),
        )
        .slice(0, 25),
    [nodes],
  );

  if (error) return <ErrorState message={error} />;
  if (!versions) return <LoadingState label="Loading taxonomy versions…" />;
  if (versions.length === 0)
    return (
      <EmptyState
        title="No taxonomy versions"
        description="No taxonomy has been authored in this environment yet."
      />
    );

  const paged = (nodes ?? []).slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={selected ?? undefined} onValueChange={setSelected}>
          <SelectTrigger className="w-72" aria-label="Taxonomy version">
            <SelectValue placeholder="Select taxonomy version" />
          </SelectTrigger>
          <SelectContent>
            {versions.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.taxonomy_key} {v.version_label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {version && <LifecycleBadge status={version.status} />}
        {version && (
          <span className="text-sm text-muted-foreground">
            Content hash <HashLabel value={version.content_hash} />
          </span>
        )}
      </div>

      {version && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Nodes" value={version.node_count} />
          <StatCard label="Edges" value={version.edge_count} />
          <StatCard label="Aliases" value={version.alias_count} />
          <StatCard
            label="Universal cyber capabilities"
            value={universal}
            hint="Nodes carrying scope = universal"
          />
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Nodes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Input
              className="w-72"
              placeholder="Search label, key or alias"
              aria-label="Search taxonomy nodes"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-56" aria-label="Filter by node kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All node kinds</SelectItem>
                {KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {k.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!nodes ? (
            <LoadingState label="Loading nodes…" />
          ) : nodes.length === 0 ? (
            <EmptyState
              title="No matching nodes"
              description="No node in this taxonomy version matches the current filters."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Aliases</TableHead>
                    <TableHead>Relationships</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map((n) => (
                    <TableRow key={n.node_id}>
                      <TableCell className="font-medium">{n.label}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {n.kind.replace(/_/g, " ")}
                      </TableCell>
                      <TableCell className="text-sm">{n.parent_label ?? "—"}</TableCell>
                      <TableCell>
                        {(n.attributes as Record<string, unknown> | null)?.scope ? (
                          <Badge variant="secondary">
                            {String((n.attributes as Record<string, unknown>).scope)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">Contextual</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {n.aliases.length > 0 ? n.aliases.slice(0, 3).join(", ") : "—"}
                        {n.aliases.length > 3 && ` +${n.aliases.length - 3}`}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(n.outgoing_edges ?? []).length === 0
                          ? "—"
                          : (n.outgoing_edges ?? [])
                              .slice(0, 3)
                              .map((e) => `${e.edge_type} → ${e.to_label}`)
                              .join("; ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Showing {page * pageSize + 1}–
                  {Math.min((page + 1) * pageSize, nodes.length)} of {nodes.length}
                </span>
                <div className="flex gap-2">
                  <button
                    className="rounded border px-2 py-1 disabled:opacity-50"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="rounded border px-2 py-1 disabled:opacity-50"
                    disabled={(page + 1) * pageSize >= nodes.length}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Explainability chains</CardTitle>
        </CardHeader>
        <CardContent>
          {chains.length === 0 ? (
            <EmptyState
              title="No chains in the current filter"
              description="Business capability → implemented_by → infrastructure component chains appear when business capabilities are in view."
            />
          ) : (
            <ul className="space-y-1 text-sm">
              {chains.map((c, i) => (
                <li key={i}>
                  <span className="font-medium">{c.capability}</span>
                  <span className="text-muted-foreground"> → implemented_by → </span>
                  <span>{c.component}</span>
                  <span className="text-muted-foreground"> → secured_by → cybersecurity capability</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {version && (
        <details>
          <summary className="cursor-pointer text-sm text-muted-foreground">
            Advanced — raw version record
          </summary>
          <div className="mt-2">
            <AdvancedJson data={version} />
          </div>
        </details>
      )}
    </div>
  );
}
